import React, { useCallback, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { Dimmer, Loader } from 'semantic-ui-react'
import { RootState } from '../../../store/rootState'
import { ICheckTokenUuidCredentials, IPasswordResetCredentilas } from '../../../types/auth'
import { ICustomizedLocationState } from '../../../types/customLocation'
import { IMenuItem } from '../../../types/menuItems'
import { IUserDetail } from '../../../types/user'
import { projectSubmenu } from '../../../utils/projectSubmenu'
import ProtectedHeaderRoutes from '../../ProtectedHeaderRoutes'
import AppRouter from '../../AppRouter'
import PublicRoutes from '../../PublicRoutes'
import * as loginActions from '../../login/actions/authActions'
import { IResetConf } from '../../login/types/resetConf'
import * as notificationActions from '../../notifications/actions/notificationActions'
import { INotificationData } from '../../notifications/types/notification'
import * as activitiesActions from '../../project/actions/activitiesActions'
import * as projectActions from '../../project/actions/projectActions'
import * as projectsActions from '../../project/actions/projectsActions'
import { _initialActivity } from '../../project/reducers/activitiesReducer'
import { IActivitySearch } from '../../project/types/activityFilters'
import { IProject, InsightsViewType, ViewType } from '../../project/types/project'
import { _initialProject } from '../../../fixtures/projectFixtures'
import { IProjectActivity } from '../../project/types/projectActivity'
import * as employeeActions from '../../settings/actions/employeeActions'
import * as mainActions from '../actions/sidebarActions'
import SidebarIcons from '../components/SidebarIcons'
import SidebarPushable from '../components/SidebarPushable'
import { useAxiosInterceptors } from '../../../api/service'
import { useWindowSize } from '../../../hooks/useWindowSize'

// Development bypass imports
import BudgetSettingsContainer from '../../budget/containers/BudgetSettingsContainer'
import StudyBudgetingContainer from '../../budget/containers/StudyBudgetingWithErrorBoundary'

interface StateProps {
  user: IUserDetail | null
  loggedIn: boolean
  sidebarVisibility: boolean
  activeMenuItem: IMenuItem
  activeSubMenuItem: IMenuItem
  activeDropdownMenuItem: IMenuItem
  selectedProject: IProject
  route: string
  resetConf: IPasswordResetCredentilas
  viewType: ViewType
  selectedActivity: IProjectActivity
  openModal: boolean
  formOpen: boolean
  formDirty: boolean
  notifications: INotificationData[]
  checkProjectLoader: boolean
}

interface DispatchProps {
  logout: () => void
  getLoggedInUser: () =>  Promise<IUserDetail>
  toggleSidebar: (visible: boolean) => void
  setActiveMenuItem: (item: string, path: string) => void
  setActiveSubMenuItem: (item: string, path: string) => void
  setActiveDropdownMenuItem: (item: string, path: string) => void
  selectProject: (project: IProject) => void
  setInsightsViewType: (viewType: InsightsViewType) => void
  resetPassword: (reset: string) => void
  setPasswordResetCredentilas: (resetConf: IResetConf) => void
  checkResetPasswordToken: (checkCredentialsToken: ICheckTokenUuidCredentials) => Promise<ICheckTokenUuidCredentials>
  setSelectedActivity: (activity: IProjectActivity) => void
  getProject: (id: string | number) => Promise<IProject>
  setProjectSettingsGeneralForm: (open: boolean) => void
  setOpenConfirmModal: (open: boolean) => void
  setFormOpen: (open: boolean) => void
  setFormDirty: (dirty: boolean) => void
  filterEmployees: (search: string) => void
  setViewType: (viewType: ViewType) => void
  searchActivities: (filter: IActivitySearch) => void
  getNotifications: (queryParams?: Record<string, string>) => void
  setProjectLoader: (load: boolean) => void
}

type Props = StateProps & DispatchProps

const MainContainer = (props: Props) => {
  const {
    sidebarVisibility,
    user,
    activeMenuItem,
    activeSubMenuItem,
    selectedProject,
    activeDropdownMenuItem,
    route,
    loggedIn,
    viewType,
    selectedActivity,
    openModal,
    formOpen,
    formDirty,
    notifications,
    checkProjectLoader,
    setOpenConfirmModal,
    getLoggedInUser,
    setActiveMenuItem,
    toggleSidebar,
    setActiveSubMenuItem,
    selectProject,
    setPasswordResetCredentilas,
    checkResetPasswordToken,
    resetPassword,
    logout,
    setInsightsViewType,
    setActiveDropdownMenuItem,
    setSelectedActivity,
    getProject,
    setFormOpen,
    setFormDirty,
    filterEmployees,
    setViewType,
    searchActivities,
    getNotifications,
    setProjectLoader
  } = props

  const _location = useLocation()
  const _history = useHistory()
  const { width: viewportWidth } = useWindowSize()

  const [load, setLoad] = useState(checkProjectLoader)

  useEffect(() => {
    useAxiosInterceptors(_history)
  }, [])

  useEffect(() => {
    setLoad(checkProjectLoader)
  }, [checkProjectLoader])

  // Handle logout when not logged in OR when accessing password reset URL
  useEffect(() => {
    const _localStorageLoggedIn = !!localStorage.getItem('logged-in')
    const isPasswordResetUrl = _location.pathname.startsWith('/password-reset')

    // If user is logged in and visiting a password reset URL, log them out first
    // This ensures the password reset flow starts fresh (e.g., admin creating user and testing reset link)
    if (_localStorageLoggedIn && isPasswordResetUrl) {
      logout()
      return
    }

    if (!_localStorageLoggedIn) {
      if (!_location.pathname.split('/').some(i => i === 'password-reset' || i === 'login')) {
        console.log('[MainContainer] Not logged in, logging out')
        logout()
      }
    }
  }, [_location, logout])

  useEffect(() => {
    const _locArr = _location.pathname.split('/').filter(path => path !== '')
    const id = _locArr.length > 2 ? _locArr[1] : ''
    if (_locArr.length > 2 && _locArr.includes('projects') && +selectedProject.id !== +id) {
      setProjectLoader(true)
      getProject(+id).then(() => setProjectLoader(false)).catch(() => setProjectLoader(false))
    }
  }, [getProject, _location.pathname, selectedProject, setProjectLoader])

  useEffect(() => {
    if (activeMenuItem.name === '') {
      const item = _location.pathname.split('/')[1]
      setActiveMenuItem(item, _location.pathname)
    }
    const _locArr = _location.pathname.split('/')
    const parentRoute = _locArr[1]
    // TODO: move to helpers
    const subRoute = (
      _locArr.includes('insights')
      || _locArr.includes('dashboard')
      || _locArr.includes('budget')
      || _locArr.includes('settings')
      || _locArr.includes('accounting'))
      && _locArr.length > 4
      ? _locArr[_locArr.length - 2] + `/${_locArr[_locArr.length - 1]}`
      : _locArr[_locArr.length - 1]
    if (activeMenuItem.name !== 'notification') {
      setActiveMenuItem(parentRoute, `/${parentRoute}`)
    }
    if (_locArr.length > 2) {
      const _locationState = _location.state as ICustomizedLocationState
      const menuItem = subRoute.split('/').filter(item => item !== '').length !== 0 ? subRoute.split('/')[0] : subRoute
      const menuItemFromLocation = _locationState && _locationState.from ? _locationState.from : menuItem
      if (!_locArr.includes('insights') && _locArr.length !== 6) {
        if (subRoute !== 'accounting') {
          setActiveSubMenuItem(menuItemFromLocation, `/${subRoute}`)
        } else {
          setActiveSubMenuItem(subRoute, `/${subRoute}`)
        }
      } else {
        if (_locArr.length !== 6) {
          setActiveSubMenuItem(menuItemFromLocation, `/${subRoute}`)
        }
      }
    } else {
      if (subRoute === 'admin') {
        setActiveSubMenuItem('user', `/${subRoute}`)
      } else {
        setActiveSubMenuItem('', '')
      }
    }
    if (_location.pathname !== `/projects/${selectedProject.id}/${subRoute}`
      && selectedProject.id
      && !_locArr.includes('payment-list')
      && !_locArr.includes('invoice-list')
      && !_locArr.includes('accounting')
      && _locArr.includes('project/')) {
      if (selectedActivity.id !== '') {
        setSelectedActivity(_initialActivity)
      }
      searchActivities({ search: '' })
      // _initialProject, never `{} as IProject`: that cast leaves financial_calculation
      // undefined, which the accounting selectors dereference (issue #20 review, claim 2).
      selectProject(_initialProject)
    }
  }, [_location, selectProject, selectedProject.id,
    setActiveMenuItem, setActiveSubMenuItem, searchActivities,
    activeMenuItem.name, selectedActivity.id, setSelectedActivity])

  useEffect(() => {
    const _locArr = _location.pathname.split('/').filter(path => path !== '')
    const id = _locArr.length > 2 ? _locArr[1] : ''
    if ((_locArr.length > 2 && _locArr.includes('projects') && +selectedProject.id === +id) || _locArr.includes('admin')) {
      if (!_locArr.includes('insights') && !_locArr.includes('settings')) {
        const isProjectOffer = _location.pathname.includes('project-offer')
        if (isProjectOffer && viewportWidth < 1600) {
          toggleSidebar(false)
        } else {
          toggleSidebar(true)
        }
      }
    } else {
      toggleSidebar(false)
    }
  }, [toggleSidebar, _location, selectedProject, viewportWidth])

  useEffect(() => {
    const element = document.getElementById('main-content')
    if (element) {
      element.scrollTop = 0
    }
  }, [_location])

  useEffect(() => {
    /**
     * Reset password navigation based on location
     */
    const _resetPassword = () => {
      // eslint-disable-next-line
      const [initString = '', location = '', uidb64 = '', token = ''] = _location.pathname.split('/')
      if (location === 'password-reset') {
        checkResetPasswordToken({ uidb64: uidb64, token: token }).then(resp => {
          const data = resp as unknown
          const resetConf: IResetConf = { ...data as IResetConf, password: '', passwordTwo: '' }
          if (Object.prototype.hasOwnProperty.call(resetConf, 'uidb64') && Object.prototype.hasOwnProperty.call(resetConf, 'token')) {
            setPasswordResetCredentilas(resetConf)
            resetPassword('/passwords-part')
          } else {
            resetPassword('/reset-password')
          }
        })
      }
    }
    _resetPassword()
  }, [_location, checkResetPasswordToken, setPasswordResetCredentilas, resetPassword, logout, loggedIn])

  useEffect(() => {
    getLoggedInUser().then((user: IUserDetail) => {
      if (user.id && user.is_active) {
        getNotifications()
      }
    })
  }, [getLoggedInUser])

  const _logout = useCallback(
    () => {
      logout()
    },
    [logout],
  )

  const _showSideBar = useCallback(
    () => {
      if (Object.prototype.hasOwnProperty.call(selectedProject, 'id')) {
        toggleSidebar(!sidebarVisibility)
      }
    },
    [toggleSidebar, selectedProject, sidebarVisibility]
  )

  const _handleSelectedItem = useCallback(
    (item: string, path: string) => {
      setActiveDropdownMenuItem('', '')
      setActiveMenuItem(item, path)
    },
    [setActiveDropdownMenuItem, setActiveMenuItem]
  )

  const handleSelectedSubItem = (item: string, path: string) => {
    if (formDirty) {
      setOpenConfirmModal(true)
      setFormOpen(true)
    }
    setActiveSubMenuItem(item, path)
  }

  const handleSelectedDropdownItem = (item: string, path: string) => {
    setActiveDropdownMenuItem(item, path)
  }

  const confirmClose = (open: boolean) => {
    setOpenConfirmModal(open)
  }

  // Development mode: Set up mock authentication for budget pages
  const isDevelopment = process.env.NODE_ENV === 'development'
  const isBudgetPath = _location.pathname.startsWith('/budget')
  
  useEffect(() => {
    if (isDevelopment && isBudgetPath) {
      const existingAuth = localStorage.getItem('logged-in')
      const existingUser = localStorage.getItem('user')
      
      // Only set up authentication if not already properly configured
      if (!existingAuth || !existingUser) {
        const mockUser = {
          id: 1,
          username: 'dev-user',
          first_name: 'Development',
          last_name: 'User',
          email: 'dev@trialytix.io',
          is_superuser: true, // Make superuser to bypass all permission checks
          is_staff: true,
          is_active: true,
          groups: [],
          user_permissions: [16, 1, 2, 3, 4, 5, 6, 14, 19, 21, 22, 24, 25, 26, 27, 28],
          date_joined: new Date().toISOString(),
          employee: {
            id: 1,
            roles: [{
              id: 1000,
              name: 'Admin',
              rights: [
                { id: 16 }, { id: 1 }, { id: 2 }, { id: 3 }, { id: 4 }, { id: 5 },
                { id: 6 }, { id: 14 }, { id: 19 }, { id: 21 }, { id: 22 }, { id: 24 },
                { id: 25 }, { id: 26 }, { id: 27 }, { id: 28 }
              ]
            }]
          }
        }
        
        localStorage.setItem('logged-in', 'true')
        localStorage.setItem('user', JSON.stringify(mockUser))
        console.log('Development mode: Enhanced mock authentication with proper roles set up')
        
        // Reload the page to trigger authentication
        window.location.reload()
      }
    }
  }, [isDevelopment, isBudgetPath])

  // Set active menu item for budget pages automatically
  useEffect(() => {
    if (_location.pathname.startsWith('/budget')) {
      if (activeMenuItem.name !== 'budget' && activeMenuItem.name !== 'notification') {
        setActiveMenuItem('budget', '/budget')
      }
    }
  }, [_location.pathname, activeMenuItem.name, setActiveMenuItem])

  const mainContent = () => {
    // Check if current URL is a password reset URL - these should always use PublicRoutes
    const isPasswordResetUrl = _location.pathname.startsWith('/password-reset')

    if (!!localStorage.getItem('logged-in') && !!localStorage.getItem('user') && !isPasswordResetUrl) {
      if (route !== '/passwords-part') {
        if (!load) {
          return (
            <div className='App'>
              <SidebarIcons
                setActiveMenuItem={_handleSelectedItem}
                showSidebar={_showSideBar}
                logout={_logout}
                sidebarVisible={sidebarVisibility}
                activeMenuItem={activeMenuItem}
                selectedProject={selectedProject}
                setOpenConfirmModal={confirmClose}
                setFormOpen={setFormOpen}
                setFormDirty={setFormDirty}
                openModal={openModal}
                formOpenAndDirty={formOpen && formDirty}
                user={user}
                notifications={notifications}
                getNotifications={getNotifications}
              />
              <SidebarPushable
                setSubMenuItem={handleSelectedSubItem}
                setDropdownMenuItem={handleSelectedDropdownItem}
                visible={sidebarVisibility}
                activeSubMenuItem={activeSubMenuItem}
                activeMenuItem={activeMenuItem}
                actDropdownMenuItem={activeDropdownMenuItem}
                mainContent={
                  <AppRouter
                    loggedIn={!!localStorage.getItem('logged-in') && !!localStorage.getItem('user')}
                    user={user}
                    haveRight={selectedProject.id !== ''}
                    checkLoader={load}
                    projectAdminRight={projectSubmenu(user, selectedProject).map(i => i.name.toLowerCase())}
                  />
                }
                headerContent={headerContent()}
                selectedProject={selectedProject}
                setInsightsViewType={setInsightsViewType}
                viewType={viewType}
                setViewType={setViewType}
                setOpenConfirmModal={confirmClose}
                setFormOpen={setFormOpen}
                setFormDirty={setFormDirty}
                openModal={openModal}
                formOpenAndDirty={formOpen && formDirty}
                filterEmployees={filterEmployees}
                user={user}
              />
            </div>
          )
        }
        return (
          <Dimmer inverted active>
            <Loader inverted size='large' />
          </Dimmer>
        )
      }
    }
    return <PublicRoutes loggedIn={!!localStorage.getItem('logged-in') && !!localStorage.getItem('user')} pubPath={route} />
  }

  const headerContent = () => {
    if (activeMenuItem.path !== '/' && activeMenuItem.path !== '') {
      return (
        <ProtectedHeaderRoutes
          loggedIn={!!localStorage.getItem('logged-in') && !!localStorage.getItem('user')}
          user={user}
          haveRight={selectedProject.id !== ''}
          checkLoader={load}
          projectAdminRight={projectSubmenu(user, selectedProject).map(i => i.name.toLowerCase())}
        />
      )
    }
    return null
  }

  return mainContent()
}

const mapStateToProps = (state: RootState): StateProps => {
  return {
    user: state.authState.user,
    loggedIn: state.authState.loginStatus,
    sidebarVisibility: state.mainState.sidebarVisible,
    activeMenuItem: state.mainState.activeMenuItem,
    activeSubMenuItem: state.mainState.activeSubMenuItem,
    activeDropdownMenuItem: state.mainState.activeDropdownMenuItem,
    selectedProject: state.projectsState.project,
    route: state.authState.pubPath,
    resetConf: state.authState.resetConf,
    viewType: state.projectsState.projectPlannerViewType,
    selectedActivity: state.projectsState.selectedActivity,
    openModal: state.mainState.openConfirmModal,
    formOpen: state.mainState.formOpen,
    formDirty: state.mainState.formDirty,
    notifications: state.notificationState.notifications,
    checkProjectLoader: state.projectsState.checkProjectLoader,
    
  }
}

const mapDispatchToProps = (dispatch: loginActions.AuthThunkDispatchType
  & mainActions.MainThunkDispatchType
  & projectActions.ProjectThunkDispatchType
  & activitiesActions.ActivitiesThunkDispatchType
  & employeeActions.EmployeeThunkDispatchType
  & projectsActions.ProjectsThunkDispatchType
  & notificationActions.NotificationsThunkDispatchType): DispatchProps => {
  return {
    logout: () => dispatch(loginActions.logout()),
    getLoggedInUser: () => dispatch(loginActions.getLoggedInUser()),
    toggleSidebar: visible => dispatch(mainActions.toggleSidebar(visible)),
    setActiveMenuItem: (item: string, path: string) => dispatch(mainActions.setActiveMenuItem(item, path)),
    setActiveSubMenuItem: (item: string, path: string) => dispatch(mainActions.setActiveSubMenuItem(item, path)),
    setActiveDropdownMenuItem: (item: string, path: string) => dispatch(mainActions.setActiveDropdownMenuItem(item, path)),
    selectProject: project => dispatch(projectActions.selectProject(project)),
    getProject: id => dispatch(projectActions.getProject(id)),
    setInsightsViewType: viewType => dispatch(projectActions.setInsightsViewType(viewType)),
    resetPassword: (route) => dispatch(loginActions.resetPassword(route)),
    setPasswordResetCredentilas: (resetConf) => dispatch(loginActions.setResetConf(resetConf)),
    checkResetPasswordToken: async (checkCredentials) => dispatch(loginActions.checkResetToken(checkCredentials)),
    setSelectedActivity: activity => dispatch(activitiesActions.setSelectedActivity(activity)),
    setProjectSettingsGeneralForm: open => dispatch(projectActions.setProjectSettingsGeneralForm(open)),
    setOpenConfirmModal: open => dispatch(mainActions.setOpenConfirmModal(open)),
    setFormOpen: open => dispatch(mainActions.setFormOpen(open)),
    setFormDirty: dirty => dispatch(mainActions.setFormDrity(dirty)),
    filterEmployees: search => dispatch(employeeActions.filterEmployees(search)),
    setViewType: viewType => dispatch(projectActions.setProjectViewType(viewType)),
    searchActivities: filter => dispatch(activitiesActions.searchActivities(filter)),
    getNotifications: (queryParams?) => dispatch(notificationActions.getNotifications(queryParams)),
    setProjectLoader: load => dispatch(projectActions.setProjectLoader(load)),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(MainContainer)
